import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { durationComparisonValidator } from '@shared/validators/duration-validator';
import { numberValidator } from '@shared/validators/number-validator';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { v4 as uuidv4 } from 'uuid';
import { IDeliverableConfig, TDeliverable } from '../deliverable-list.interface';
import { GamePlayComponent } from './game-play.component';
import { IGamePlayDeliverableForm } from './game-play.interface';

@Injectable({
  providedIn: 'root',
})
export class GamePlayService {
  private readonly translate = inject(TranslateService);

  public getConfig(): IDeliverableConfig {
    return {
      component: GamePlayComponent,
      createForm: (
        item?: TDeliverable,
        creatorId?: string,
        isViewMode?: boolean,
      ): FormGroup<IGamePlayDeliverableForm> | null => {
        if (item && item.type !== EDeliverableType.gamePlay) {
          return null;
        }

        // Selecting a game from the autocomplete always sets gameId and gameName together
        // (onGameSelected), so a filled gameName with no gameId never happens through normal UI
        // use - but a brief-imported campaign can hand us exactly that (the model only knows the
        // game's name), and the backend requires gameId. Captured by closure so gameName's own
        // validator can see it without a parent-control lookup.
        //
        // gameName's validator below only re-runs when gameName's own control changes value (or
        // updateValueAndValidity() is called on it explicitly) - it does not react to gameId
        // changing on its own. Every current write site keeps the two in sync (onGameSelected sets
        // both together; onGameInputBlur clears both together and re-validates gameName itself), so
        // this doesn't misfire today - but a future write site that clears gameId alone would leave
        // gameName showing stale (valid) validity until something else touches it.
        const gameIdControl = new FormControl<number | string | null>(item?.gameId ?? null);

        const form = new FormGroup({
          boxArtUrl: new FormControl<string | null>(
            ImageUtilitiesService.replaceImageDimensions(item?.boxArtUrl, 92, 124),
            Validators.maxLength(255),
          ),
          customCtaValues: new FormControl<ICustomCtaValue[]>(item?.customCtaValues ?? []),
          duration: new FormControl<number | null>(
            item?.duration ?? null,
            isViewMode ? [] : [Validators.required, numberValidator(), durationComparisonValidator()],
          ),
          exclusiveTo: new FormControl<string | null>(item?.exclusiveTo || creatorId || null),
          gameId: gameIdControl,
          gameName: new FormControl<string | null>(
            item?.gameName ?? null,
            isViewMode
              ? []
              : [Validators.required, (): ValidationErrors | null => (gameIdControl.value ? null : { required: true })],
          ),
          globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(item?.globalCtaValues ?? null),
          id: new FormControl<string | null>(item?.id ?? uuidv4(), Validators.required),
          isNew: new FormControl<boolean>(!item?.id),
          name: new FormControl<string | null>(
            item?.name ??
              this.translate.instant('pages.campaignForm.ctaDialog.ctaDetails.deliverables.gamePlayDeliverable.title'),
            [Validators.required, Validators.maxLength(255)],
          ),
          sponsoredContentRelevant: new FormControl<boolean>(item?.sponsoredContentRelevant ?? true, {
            nonNullable: true,
          }),
          type: new FormControl<EDeliverableType.gamePlay>(EDeliverableType.gamePlay, { nonNullable: true }),
        }) as FormGroup<IGamePlayDeliverableForm>;

        if (isViewMode) {
          form.disable();
        }

        return form;
      },
    };
  }
}
